import { graphql } from '@apollo/client/react/hoc';
import { flowRight as compose } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { addBox, removeBox } from 'reducers/floatingBoxes';
import { bindActionCreators } from 'redux';
import PhoneCallFloatingBox from 'phoneCalls/components/PhoneCallFloatingBox';
import CurrentUser from 'auth/CurrentUser';
import { showToast } from 'reducers/toasts';
import type { State as PhoneCallsState } from 'phoneCalls/reducers/phoneCalls';
import { showRecent, hideRecent } from 'reducers/recentMessagesModal';
import PhoneCallInitializingView from 'phoneCalls/components/PhoneCallInitializingView';
import PhoneCallRetryView from 'phoneCalls/components/PhoneCallRetryView';
import Dialog from 'components/Dialog';

import CLICK_TO_CALL from 'phoneCalls/mutations/ClickToCall.gql';
import CANCEL_PHONE_CALL from 'phoneCalls/mutations/CancelPhoneCall.gql';

import { ClickToCall, ClickToCallVariables } from 'phoneCalls/mutations/__generated__/ClickToCall';
import {
  CancelPhoneCallVariables,
  CancelPhoneCall,
} from 'phoneCalls/mutations/__generated__/CancelPhoneCall';

class PhoneCallStartedView extends React.Component<{
  onMount: () => void;
}> {
  componentDidMount() {
    this.props.onMount();
  }
  render() {
    return null;
  }
}

type Props = {
  calleeUuid: string;
  calleeName: string;
  onRequestHide: Function;
};

type InnerProps = {
  // apollo
  cancelPhoneCall: (
    phoneCallUuid: string
  ) => Promise<CancelPhoneCall['cancelPhoneCall'] | undefined>;
  initiatePhoneCall: (
    callerUuid: string,
    calleeUuid: string
  ) => Promise<ClickToCall['clickToCall'] | undefined>;

  // redux state
  phoneCalls: PhoneCallsState;

  // redux dispatch
  showToast: (text: string, kind: 'success' | 'error') => any;
  showRecent: Function;
  hideRecent: Function;
  addBox: Function;
  removeBox: Function;
};

type State = {
  uuid: string | undefined | null;
};

export class PhoneCallModalContent extends React.Component<Props & InnerProps, State> {
  state: State = {
    uuid: null,
  };

  componentDidMount() {
    this.initializePhoneCall();
  }

  initializePhoneCall = () => {
    if (this.state.uuid) {
      this.setState({ uuid: null });
    }
    const callerUuid = CurrentUser.getUserUuid();
    this.props
      .initiatePhoneCall(callerUuid, this.props.calleeUuid)
      .then((data) => this.setState({ uuid: data?.uuid }), this.handleError)
      .catch((e) => {
        console.error('PhoneCallModalContent initializePhoneCall error', e);
        this.props.showToast('There was a problem starting your phone call.', 'error');
      });
  };

  handleError = (error: any) => {
    console.error('initiate phone call failed', error);
    this.props.showToast(error.message, 'error');
    this.props.onRequestHide();
  };

  handleCallStarted = () => {
    this.props.onRequestHide();
    this.props.addBox(
      this.state.uuid,
      <PhoneCallFloatingBox
        uuid={this.state.uuid || undefined}
        remove={() => this.props.removeBox(this.state.uuid)}
      />
    );
  };

  callCancelled = () => {
    this.props.showToast(`Call with ${this.props.calleeName} canceled!`, 'success');
    this.props.onRequestHide();
  };

  cancelCall = () => {
    if (!this.state.uuid) {
      this.callCancelled();
      return;
    }
    this.props
      .cancelPhoneCall(this.state.uuid)
      .then(this.callCancelled)
      .catch((err) => this.props.showToast('Error, ' + err.message, 'error'));
  };

  cancelRetry = () => {
    this.props.onRequestHide();
  };

  render() {
    const call = this.state.uuid ? this.props.phoneCalls.get(this.state.uuid) : null;
    const status = call ? call.status : 'initializing';
    let content;
    let hero;
    let onDialogDismiss;
    let hideCloseButton = false;
    switch (status) {
      case 'initializing':
        content = (
          <PhoneCallInitializingView name={this.props.calleeName} onCancel={this.cancelCall} />
        );
        hero = '/assets/images/phoneCalls/PhoneCallModal/click-to-call-answer.svg';
        onDialogDismiss = this.cancelCall;
        hideCloseButton = true;
        break;
      case 'caller_unreachable':
        content = (
          <PhoneCallRetryView
            name={this.props.calleeName}
            onCancel={this.cancelRetry}
            onRetry={this.initializePhoneCall}
          />
        );
        hero = '/assets/images/phoneCalls/PhoneCallModal/click-to-call-didnt-pick-up.svg';
        break;
      case 'started':
        content = <PhoneCallStartedView onMount={this.handleCallStarted} />;
        break;
      default:
        console.error('PhoneCallModalContent - error rendering unsupported status', status);
        content = <span>status not supported</span>;
        break;
    }
    return (
      <Dialog
        eventScreenName="start_phone_call"
        hideOnEscape={false}
        hideOnBlur={false}
        hideCloseButton={hideCloseButton}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        onRequestHide={this.props.onRequestHide}
        size="small"
        onDialogDismiss={onDialogDismiss}
        headerImage={hero || undefined}
      >
        {content}
      </Dialog>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    phoneCalls: state.phoneCalls,
  };
}

function mapDispatchToProps(dispatch: Function) {
  // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
  return bindActionCreators({ showToast, showRecent, hideRecent, addBox, removeBox }, dispatch);
}

const withMutations = graphql<
  {},
  ClickToCall,
  ClickToCallVariables,
  Pick<InnerProps, 'initiatePhoneCall'>
>(CLICK_TO_CALL, {
  props({ mutate }) {
    return {
      async initiatePhoneCall(callerUuid, calleeUuid) {
        const input = { callerUuid, calleeUuid };
        const res = await mutate?.({ variables: { input } });
        const error = res?.data?.clickToCall?.error;
        if (error) {
          throw new Error(error.message);
        }

        return res?.data?.clickToCall;
      },
    };
  },
});

const withCancelMutations = graphql<
  {},
  CancelPhoneCall,
  CancelPhoneCallVariables,
  Pick<InnerProps, 'cancelPhoneCall'>
>(CANCEL_PHONE_CALL, {
  props({ mutate }) {
    return {
      async cancelPhoneCall(phoneCallUuid) {
        const input = { phoneCallUuid };
        const res = await mutate?.({ variables: { input } });
        const error = res?.data?.cancelPhoneCall?.error;
        if (error) {
          throw new Error(error.message);
        }
        return res?.data?.cancelPhoneCall;
      },
    };
  },
});

export default compose(
  withCancelMutations,
  withMutations,
  connect(mapStateToProps, mapDispatchToProps)
)(PhoneCallModalContent) as React.ComponentType<Props>;
